import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { MessageSquare, X, CheckCheck, ExternalLink } from "lucide-react";
import { api } from "../lib/api";

const TYPE_COLOR = {
  booking_confirmed: "#13A699",
  booking_declined: "#DC2626",
  course_cancelled: "#DC2626",
  demo_cancelled: "#DC2626",
  payment: "#FFD708",
  reminder: "#FFD708",
};

function relTime(iso) {
  try {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} h ago`;
    return `${Math.floor(diff / 86400)} d ago`;
  } catch {
    return "";
  }
}

export default function NotificationInbox() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const popRef = useRef(null);

  const load = async () => {
    // Short-circuit when no auth token exists (post-logout) to avoid 401 console noise
    try {
      if (typeof window !== "undefined" && !localStorage.getItem("rateed_access_token")) return;
    } catch { /* ignore */ }
    try {
      const { data } = await api.get("/me/notifications");
      setItems(data || []);
    } catch {
      /* silent */
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, []);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (popRef.current && !popRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const unreadCount = items.filter((n) => !n.read).length;

  const markRead = async (id) => {
    setItems((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
    try {
      await api.post(`/me/notifications/${id}/read`);
    } catch { /* silent */ }
  };

  const markAllRead = async () => {
    setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    try {
      await api.post(`/me/notifications/read-all`);
    } catch { /* silent */ }
  };

  return (
    <div className="relative" ref={popRef} data-testid="notification-inbox">
      <button
        type="button"
        onClick={() => { setOpen((o) => !o); if (!open) load(); }}
        className="relative inline-flex items-center justify-center h-9 w-9 rounded-lg border border-[#13A699] hover:bg-[#13A699] hover:text-white transition-colors"
        aria-label={`Notifications${unreadCount ? ` (${unreadCount} unread)` : ""}`}
        data-testid="notification-bell"
        style={{ color: "#13A699" }}
      >
        <MessageSquare className="h-4 w-4" />
        {unreadCount > 0 && (
          <span
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full text-[10px] font-bold flex items-center justify-center px-1 ring-2 ring-[#FFF7ED]"
            style={{ background: "#DC2626", color: "#FFFFFF" }}
            data-testid="notification-unread-badge"
          >
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-[340px] sm:w-[400px] max-h-[70vh] overflow-y-auto rounded-3xl bg-white shadow-2xl border border-black/5 z-50"
          data-testid="notification-drawer"
        >
          <div className="sticky top-0 bg-white px-4 py-3 border-b border-black/5 flex items-center justify-between gap-2">
            <h3 className="font-display text-base font-bold uppercase" style={{ color: "#13A699" }}>
              Inbox
            </h3>
            <div className="flex items-center gap-1">
              {unreadCount > 0 && (
                <button
                  type="button"
                  onClick={markAllRead}
                  className="text-[11px] font-bold inline-flex items-center gap-1 px-2 py-1 rounded-full hover:bg-[#FFF7ED]"
                  style={{ color: "#13A699" }}
                  data-testid="notification-mark-all-read"
                >
                  <CheckCheck className="h-3 w-3" /> Mark all read
                </button>
              )}
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="p-1 rounded-full hover:bg-black/5"
                aria-label="Close inbox"
                data-testid="notification-close"
              >
                <X className="h-4 w-4" style={{ color: "#13A699" }} />
              </button>
            </div>
          </div>

          {items.length === 0 ? (
            <div className="p-6 text-center text-sm" style={{ color: "#13A69999" }} data-testid="notification-empty">
              You&apos;re all caught up — no notifications yet.
            </div>
          ) : (
            <ul className="divide-y divide-black/5">
              {items.map((n) => {
                const dot = TYPE_COLOR[n.type] || "#13A699";
                return (
                  <li
                    key={n.id}
                    className={`px-4 py-3 hover:bg-[#FFF7ED] transition-colors ${n.read ? "opacity-70" : ""}`}
                    data-testid={`notification-item-${n.id}`}
                    onClick={() => !n.read && markRead(n.id)}
                  >
                    <div className="flex items-start gap-3">
                      <span
                        className="h-2 w-2 rounded-full mt-1.5 shrink-0"
                        style={{ background: n.read ? "#13A69955" : dot }}
                        aria-hidden="true"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-bold text-sm" style={{ color: "#13A699" }}>{n.title}</div>
                        {n.message && (
                          <div className="text-xs mt-0.5 break-words" style={{ color: "#13A699BB" }}>{n.message}</div>
                        )}
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-[10px] uppercase font-bold" style={{ color: "#13A69988" }}>
                            {relTime(n.created_at)}
                          </span>
                          {n.action_url && (
                            <Link
                              to={n.action_url}
                              onClick={() => setOpen(false)}
                              className="text-[10px] font-bold inline-flex items-center gap-0.5 hover:underline"
                              style={{ color: "#13A699" }}
                              data-testid={`notification-link-${n.id}`}
                            >
                              View <ExternalLink className="h-2.5 w-2.5" />
                            </Link>
                          )}
                        </div>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
